import { useCallback, useMemo, useState } from 'react'
import { useLocation } from 'react-router'
import { FilterFieldGroup } from '@App/forms/common/filter-field-group'
import { FilterValues } from '@Components/Search/SearchPanel/types'
import {
    getFilterFieldMeta,
    parseFiltersFromSearch,
    serializeFiltersToSearch,
} from '@Components/Search/SearchPanel/filter-url-params'
import useUrlSearchWriter from '@Components/Search/SearchPanel/useUrlSearchWriter'

export interface UseFilterUrlSyncResult<K extends object> {
    /**
     * Filter values parsed from the URL's query string on first render; undefined if none of the
     * known filter fields were present. Read once — the URL becomes a reflection of in-app filter
     * state (via `syncFiltersToUrl`) once the page has mounted, rather than something re-read live.
     */
    urlFilterValues: FilterValues<K> | undefined
    /**
     * Writes `filters` into the URL's query string, keyed by filter field name, so the current
     * address bar is always a valid link back to this view. Pass `undefined` to clear them (e.g.
     * on a full "Reset Search"). Unrelated query params and the current hash (e.g. a tab selector)
     * are preserved; the navigation replaces history rather than pushing a new entry.
     */
    syncFiltersToUrl: (filters: FilterValues<K> | undefined) => void
}

/**
 * Keeps a SearchPanel's applied filters reflected in the URL's query string, so a page can be
 * linked to with filters pre-set (e.g. a dashboard card linking into a pre-filtered search).
 *
 * This is opt-in glue a page wires itself — nothing here reaches into SearchPanel internals:
 * seed the initial search/results with `urlFilterValues` before the first fetch runs, pass
 * `defaultFilterValues={urlFilterValues ?? ...pageDefaults}` and `onFilterValueChange={syncFiltersToUrl}`
 * to SearchPanel, and have the page's `onReset` also call `syncFiltersToUrl(undefined)`.
 *
 * @template {object} K - The record type for the SearchPanel's "filter" portion, same as its own.
 */
export const useFilterUrlSync = <K extends object>(
    filterFieldCollections: Array<FilterFieldGroup<K>> | undefined
): UseFilterUrlSyncResult<K> => {
    const location = useLocation()
    const writeSearch = useUrlSearchWriter()

    const fields = useMemo(() => getFilterFieldMeta(filterFieldCollections), [filterFieldCollections])
    const [urlFilterValues] = useState(
        () => parseFiltersFromSearch(location.search, fields) as FilterValues<K> | undefined
    )

    const syncFiltersToUrl = useCallback(
        (filters: FilterValues<K> | undefined) =>
            writeSearch((currentSearch) =>
                serializeFiltersToSearch(currentSearch, filters as Record<string, unknown> | undefined, fields)
            ),
        [fields, writeSearch]
    )

    return { urlFilterValues, syncFiltersToUrl }
}

export default useFilterUrlSync
